import React from 'react';
import styled from 'styled-components';

import { IconButton } from '@/ui/Button';
import { Icon } from '@/ui/Icon';

const RootStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px 20px;
  border: 1px solid ${(p) => p.theme.colors.primary.main};
  border-radius: 8px;
  background-color: ${(p) => p.theme.tones.primary[99]};
`;

const IconStyled = styled(Icon)`
  flex-shrink: 0;
  font-size: 24px;
  color: ${(p) => p.theme.colors.primary.main};
`;

const DetailsStyled = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/* The legacy global stylesheet still puts em-based margins on paragraphs */
const FileNameStyled = styled.p`
  ${(p) => p.theme.typography.title5};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const RowCountStyled = styled.p`
  ${(p) => p.theme.typography.caption};
  margin: 0;
  color: ${(p) => p.theme.colors.primary.main};
`;

type CsvImportSelectedFileCardProps = {
  fileName: string;
  /**
   * Data rows counted in the browser, shown before anything is uploaded. Omitted while the
   * count is still being read off the file.
   */
  rowCount?: number;
  onRemove: VoidFunction;
  /** Removing a file mid-upload would leave the response with nowhere to go. */
  removeDisabled?: boolean;
  className?: string;
};

/**
 * The file staged on the upload step, replacing the drop area once something is chosen.
 */
export const CsvImportSelectedFileCard = ({
  fileName,
  rowCount,
  onRemove,
  removeDisabled = false,
  className,
}: CsvImportSelectedFileCardProps): JSX.Element => (
  <RootStyled className={className}>
    <IconStyled icon={['far', 'file-lines']} aria-hidden />
    <DetailsStyled>
      <FileNameStyled title={fileName}>{fileName}</FileNameStyled>
      {rowCount !== undefined && <RowCountStyled>{`${rowCount} ${rowCount === 1 ? 'row' : 'rows'}`}</RowCountStyled>}
    </DetailsStyled>
    <IconButton
      icon="xmark"
      buttonVariant="buddySmall"
      onClick={onRemove}
      disabled={removeDisabled}
      aria-label={`Remove ${fileName}`}
    />
  </RootStyled>
);
